/// <reference path="./ovs.core.d.ts" />

import { CurrencyPipe, Location } from '@angular/common';
import { FormControl, NG_VALIDATORS, Validator, AbstractControl, ValidatorFn } from '@angular/forms';
import { HttpClient, HttpResponse, HttpParams, HttpParameterCodec, HttpHeaders } from '@angular/common/http';
import { ActivatedRoute, Router, NavigationExtras, RoutesRecognized, NavigationEnd, ActivatedRouteSnapshot, ActivationEnd } from '@angular/router';
import { OnDestroy, Injector, OnInit, NgZone, HostListener, Injectable, OnChanges, SimpleChanges, SimpleChange, Pipe, PipeTransform, Directive, Input } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subscription } from 'rxjs/Subscription';
import { CardTypes } from '../../models/card-types.enum';
import { CookieDecoratorData } from '@ngx-toolkit/cookie/src/cookie.decorator';
import { CookieService } from '@ngx-toolkit/cookie';
import { StorageService } from '../../services/storage-service.client';
import { ResortInsightsService } from '../../instrumentation/resort-insights.service';
import { EncoreService } from '../../services/encore.service';
import { AppInsightsService, AppInsightsModule } from 'microsoft-applicationinsights-angular5';
import { Guid } from "guid-typescript";
import { EnvironmentConfigurationService } from '../../services/environment-configuration.service';
import { ContentApiClient } from '../../services/content-service';
import { SearchProperties } from '../../instrumentation/tealium/tealium-data-points.model';
import { catchError } from 'rxjs/operators';
import { ErrorObservable } from 'rxjs/observable/ErrorObservable';

export class LabelNames {
    public static Welcome: string = "welcome";
    public static SoftCurrenyLongName: string = "soft_currency_long_name";
    public static SoftCurrenyShortName: string = "soft_currency_short_name";
    public static ContactHeaderText: string = "contact_header_text";
    public static Balance: string = "balance";
    public static MarketPrice: string = "market_price";
    public static MemberPrice: string = "member_price";
    public static RetailPrice: string = "retail_price";
    public static CustomerPrice: string = "customer_price";
    public static TaxesFees: string = "taxes_fees";
    public static TotalPrice: string = "total_price";
    public static CustomerSave: string = "customer_save";
    public static SubTotal: string = "sub_total";
    public static BalancePaid: string = "balance_paid";
    public static Points: string = "points";
    public static VacationCash: string = "vacation_cash";
    public static PhoneNumber: string = "phone_number";
    public static AgentId: string = "agent_id";
    public static Agent: string = "agent";
    public static Dbc: string = "dbc";
    public static AccountSettingsLabel: string = "account_settings_label"; //**
    public static Login: string = "login";
    public static SignOut: string = "sign_out";
    public static ThirdPartyHomeLabel: string = "third_party_home_label";
    public static HeaderBadgeLabel: string = "header_badge_label";
    public static HeaderBadgeButtonText: string = "header_badge_button_text";
    public static FirstName: string = "first_name";
    public static LastName: string = "last_name";
    public static Address1: string = "address1";
    public static Address2: string = "address2";
    public static City: string = "city";
    public static Region: string = "state_province";
    public static Country: string = "country";
    public static PostalCode: string = "postal_code";
    public static SubmitButtonLabel: string = "submit_button_label";
    public static SubmitButtonProcessText: string = "submit_button_process_text";
    public static BookButtonLabel: string = "book_button_label";
    public static BookButtonProcessingText: string = "book_button_processing_text";
    public static Copyright: string = "copyright";
    public static MyAccountHRef: string = "my_account_href"; //**
    public static MyAccountText: string = "account_settings_label";
    public static RedeemText: string = "redeem_label";
    public static RedeemHRef: string = "redeem_href";
    public static SignOutText: string = "sign_out_label";
    public static SignOutHRef: string = "sign_out_href";
    public static Sot: string = "sot";
    public static SearchDisclaimerLabel: string = "search_disclaimer";
    public static SearchDisclaimerNoResultsLabel: string = 'search_disclaimer_no_results';
    public static ConfirmationPercentBadgeLabel: string = "confirmation_percent_badge_label";
    public static MarketingOptInLabel: string = "guest_text_messaging_approved_text";
}

export enum DisplaySize {
    None = 0,
    ExtraSmall = 1,
    Small = 2,
    Medium = 3,
    Large = 4,
    ExtraLarge = 5
}

export class Selectable<T>
{
    public value: T;
    public isSelected: boolean;

    constructor(value: T, isSelected: boolean = false) {
        this.value = value;
        this.isSelected = isSelected;
    }
}

export class Core {
    private static _core: Core;
    private _injector: Injector;
    private _application: IApplication;
    private _errorHandler: ErrorHandler;

    // Need these to be a *hot* observable with state.
    private _configuration: BehaviorSubject<ApplicationConfiguration> = new BehaviorSubject<ApplicationConfiguration>(new ApplicationConfiguration());
    private _isLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public get configuration(): Observable<ApplicationConfiguration> { return this._configuration.asObservable(); }
    public get currentConfiguration(): ApplicationConfiguration { return this._configuration.value; }
    public get isLoaded(): Observable<boolean> { return this._isLoaded.asObservable(); }
    public get application(): IApplication { return this._application; }
    public static get current(): Core { return this._core; }

    public static Run<T>(application: IApplication, injector: Injector): void {
        Queryable.initialize();

        // Core should have been added to the providers list in the main module.  Get the singleton instance.
        Core._core = injector.get(Core);
        Core._core._injector = injector;
        Core._core.start(application);
    }

    public start(application: IApplication): void {
        let router: Router = this.getRequiredService<Router>(Router);
        let http: HttpClient = this.getRequiredService<HttpClient>(HttpClient);
        let urlService: UrlService = <UrlService>this.getRequiredService<UrlServiceBase>(UrlServiceBase);
        let qryParamService: QueryParamService = <QueryParamService>this.getRequiredService<QueryParamService>(QueryParamService);
        let configService: EnvironmentConfigurationService = <EnvironmentConfigurationService>this.getRequiredService<EnvironmentConfigurationService>(EnvironmentConfigurationService);
        this._errorHandler = this.getRequiredService<ErrorHandler>(ErrorHandler);
        this._application = application;


        // Get the parameters for the page.  We are outside of the route, so we need to iterate through the router events.
        let sub = router.events.subscribe(val => {
            if (val instanceof RoutesRecognized || (val instanceof ActivationEnd)) {
                sub.unsubscribe();
                if (val instanceof RoutesRecognized) {
                    let params = val.state.root.firstChild.queryParams;
                    qryParamService.intialize(params);
                }
                if (val instanceof ActivationEnd) {
                    let params = val.snapshot.queryParams;
                    qryParamService.intialize(params);
                }
                ResortInsightsService.setBaseEventData(qryParamService.applicationParams);

                //  Get the configuration file from the angular CLI package (location) that holds the root (backend service) URL for the application.
                http.get<any>('config.json', { observe: 'response' })
                    .subscribe((value: HttpResponse<any>) => {
                        if (value.ok) {
                            configService.initialize(value.body);

                            let appInsights: AppInsightsService = new AppInsightsService(configService.configuration.applicationInsightsInstrumentationKey);
                            ResortInsightsService.intialize(appInsights);
                            //TealiumService.intialize(configService.configuration.tealiumConfiguration);
                            EncoreService.initialize(configService.configuration.encoreConfiguration);
                            urlService.setRootUrl(configService.configuration.rootUrl);

                            // Call the product specific custom application object so it can set up its configuration, etc.
                            // The application object should call back on the passed function on completion (error or not).
                            application.configure(qryParamService.applicationParams, (r) => this.onConfigureCompleted(r));
                        }
                    },
                        (e) => {
                            this._errorHandler.handleError(e);
                        });
            }
        });
    }

    protected onConfigureCompleted(result: ApplicationConfiguration | Error): void {
        if (result instanceof ApplicationConfiguration) {
            let zone: NgZone = this._injector.get(NgZone);
            zone.run(() => {
                this._configuration.next(result);
                this._application.load((r) => this.onLoadCompleted(r));
            });
        }
        else {
            this._errorHandler.handleError(result);
        }
    }

    protected onLoadCompleted(error?: Error): void {
        if (error) {
            this._errorHandler.handleError(error);
        }
        else {
            let zone: NgZone = this._injector.get(NgZone);
            zone.run(() => {
                this._isLoaded.next(true);
            });
        }
    }

    public getService<T>(token: any): T {
        try {
            return this._injector.get(token);
        }
        catch (error) {
            return null;
        }
    }

    public getRequiredService<T>(token: any): T {
        return this._injector.get(token);
    }

    public getLabel(key: string): string {
        if (this.currentConfiguration && this.currentConfiguration.Labels) {
            const lableObject = this.currentConfiguration.Labels.firstOrDefault(l => l.Name === key);
            if (lableObject !== null) {
                return lableObject.DisplayText;
            }
        }

        return '--######--';
    }

    public calculateDiff(checkInDate) {
        if (checkInDate) {
            var date1: any = new Date(checkInDate);
            var date2: any = new Date();
            date2.setHours(0);
            date2.setMinutes(0);
            date2.setSeconds(0);
            date2.setMilliseconds(0);

            return Math.floor((date1 - date2) / (1000 * 60 * 60 * 24)) > 31
        }
        return false;
    }
}

export abstract class DisplayServiceBase {
    abstract displaySize: Observable<DisplaySize>;
}

@Injectable()
export class DisplayService extends DisplayServiceBase {
    private _displaySize: BehaviorSubject<DisplaySize> = new BehaviorSubject<DisplaySize>(DisplaySize.None);
    private _zone: NgZone;

    public get displaySize(): Observable<DisplaySize> { return this._displaySize.asObservable(); }

    constructor(zone: NgZone) {
        super();
        this._zone = zone;
        this.setDisplaySize(this.getDisplaySize(window.innerWidth));

        //window.addEventListener("resize", (event: UIEvent) =>
        //{
        //    let width: number = (<any>event.target).innerWidth;
        //    this.setDisplaySize(this.getDisplaySize(width));
        //});
    }

    private setDisplaySize(size: DisplaySize): void {
        if (size == this._displaySize.value)
            return;

        this._zone.run(() => {
            this._displaySize.next(size);
        });
    }

    private getDisplaySize(width: number): DisplaySize {
        if (width >= 1200)
            return DisplaySize.ExtraLarge;
        else if (width >= 992)
            return DisplaySize.Large;
        else if (width >= 768)
            return DisplaySize.Medium;
        else if (width >= 576)
            return DisplaySize.Small;

        return DisplaySize.ExtraSmall;
    }
}

export abstract class ErrorHandler {
    public handleError(error: any): void {
        Core.current.application.error(error);
    }
}

export class DefaultErrorHandler extends ErrorHandler {
    public handleError(error: any): void {
        if (!error)
            error = { message: "An error has occurred." };

        if (!error.message && error.error)
            error = error.error;

        if (!error.message)
            error = { message: error };

        const globalErrorScriptId: string = "ovs_error_script_init";
        let globalScript = document.getElementById(globalErrorScriptId);
        if (!globalScript) {
            globalScript = document.createElement("script");
            globalScript.id = globalErrorScriptId;
            let inlineScript = document.createTextNode("var lastError;");
            globalScript.appendChild(inlineScript);
            document.head.appendChild(globalScript);
        }

        let script = document.createElement("script");
        let inlineScript = document.createTextNode("lastError = JSON.parse(\"" + JSON.stringify(error).replaceStr("\\", "\\\\").replaceStr("\"", "\\\"") + "\");");
        script.appendChild(inlineScript);
        document.head.appendChild(script);

        super.handleError(error);
    }
}

export interface IApplication {
    configure(params: any, completionCallback: (configuration: ApplicationConfiguration | any) => void): void;
    load(completionCallback: (error?: any) => void): void;
    error(error: any): void;
}

export abstract class UrlServiceBase {
    public abstract register(key: any, url: string): void;
    public abstract getUrl(key: any): string;
    public rootUrl: string;
}

export class ApplicationParams {
    partnerId: string;
    nameId: string;
    cobrandId: string;
    affiliatePin: string;
    agentId: string;
    authCode: string;
    certNumber: string;
    token: string;
    sessionId: string;
    timezoneOffset: number = new Date().getTimezoneOffset();

    public get hasEmptyParams(): boolean {
        return !(this.partnerId && this.nameId && this.cobrandId && this.agentId);
    }
}

export class ResortListingParams {
    searchId: string;
    isDirect: boolean;
    searchText: string;
    searchDate: Date;
    latitude: number;
    longitude: number;

    restore(source: ResortListingParams) {
        this.searchId = source.searchId;
        this.isDirect = <boolean>source.isDirect;
        this.searchText = source.searchText;
        this.searchId = source.searchId;
        if (source.searchDate != undefined && source.searchDate != null)
            this.searchDate = new Date(Date.parse(source.searchDate.toString()));
        this.latitude = <number>(source.latitude);
        this.longitude = <number>source.longitude;
    }
}

export class ResortDetailParams {
    resortId: string;
    startDate: Date;
    isExclusive: boolean;
    restore(source: any) {
        if (source != null && source != undefined) {
            this.resortId = source.resortId;
            this.isExclusive = source.isExclusive;
            if (source.startDate != undefined && source.startDate != null)
                this.startDate = new Date(Date.parse(source.startDate.toString()));
        }
    }
}

export class CheckoutParams {
    intervalId: number;
    inventoryId: string;
    restore(source: any) {
        this.intervalId = <number>source.intervalId;
        this.inventoryId = source.inventoryId;
    }
}

@Injectable()
export class QueryParamService {
    private _jwt: string = null;
    private _partnerId: string = null;
    private _nameId: string = null;
    private _cobrandId: string = null;
    private _affiliatePin: string = null;
    private _agentId: string = null;
    private _authcode: string = null;
    private _authId: string = null;
    private _certnumber: string = null;
    private _timezoneOffset: number = null;
    private _searchId: string = null;
    private _storageKey: string = "";
    private _guid: string;
    private _sessionId: string;

    private _applicationParams: ApplicationParams = new ApplicationParams();
    private _resortListingParams: ResortListingParams = new ResortListingParams();
    private _resortDetailParams: ResortDetailParams = new ResortDetailParams();
    private _checkoutParams: CheckoutParams = new CheckoutParams();

    public get applicationParams(): ApplicationParams {
        return this._applicationParams;
    }

    public get resortListingParams(): ResortListingParams {
        return this._resortListingParams;
    }

    public get resortDetailParams(): ResortDetailParams {
        return this._resortDetailParams;
    }

    public get checkoutParams(): CheckoutParams {
        return this._checkoutParams;
    }

    public get jwt(): string {
        return this._applicationParams.token;
    }

    public partnerId(): string {
        return this._applicationParams.partnerId;
    }

    public nameId(): string {
        return this._applicationParams.nameId;
    }

    public cobrandId(): string {
        return this._applicationParams.cobrandId;
    }

    public affiliatePin(): string {
        return this._applicationParams.affiliatePin;
    }

    public agentId(): string {
        return this._applicationParams.agentId;
    }

    public authcode(): string {
        return this._applicationParams.authCode;
    }

    public certnumber(): string {
        return this._applicationParams.certNumber;
    }

    public timezoneOffset(): number {
        return this._applicationParams.timezoneOffset;
    }

    public searchId(): string {
        return this.resortListingParams.searchId;
    }

    public get encodedGuid(): string {
        return btoa(this._guid);
    }

    public get hasEmptyParams(): boolean {
        return this.applicationParams.hasEmptyParams;
    }

    protected getParameterByName<T>(params: { [key: string]: any; }, name: string, isRequired: boolean = false, type: { new(): T; }): T {
        let value: any;

        name = name.toLowerCase();
        for (let key in params) {
            if (key.toLowerCase() === name)
                value = params[key];
        }

        if (isRequired && (!value || String.isNullOrWhiteSpace(value)))
            throw new Error("Parameter missing:  " + name);

        if (value === undefined)
            value = "";

        if (<any>type === String)
            return value;

        if (<any>type === Boolean)
            return <any>new Boolean(value);

        if (<any>type === Date)
            return <any>new Date(Date.parse(value));

        if (<any>type === Number)
            return <any>new Number(value);

        let typedValue: T = new type();

        if (TypeHelpers.IsModel(typedValue)) {
            let entity: Entity = <Entity><any>typedValue;
            entity.mapFrom(value);
            return typedValue;
        }

        return JSON.parse(value);
    }



    public intialize(params: any) {

        let sid: string = <string>this.getParameterByName(params, "sid", false, String);

        // verify if partnerId, cobrandId and nameId changed.
        if (this.applicationParams) {

            // get partnerId, cobrandId and nameId from query string
            let partnerId = <string>this.getParameterByName(params, "partnerId", false, String);
            let cobrandId = <string>this.getParameterByName(params, "cobrandId", false, String);
            let nameId = <string>this.getParameterByName(params, "nameId", false, String);


            if ((partnerId !== this._applicationParams.partnerId
                || cobrandId !== this._applicationParams.cobrandId
                || nameId !== this._applicationParams.nameId)
                && (sid === undefined || sid === null || sid === "")) {
                this.applicationParams.partnerId = partnerId;
                this.applicationParams.cobrandId = cobrandId;
                this.applicationParams.nameId = nameId;
                this.store();
            }
        }

        if (this._guid == "" || this._guid == null || this._guid == undefined) {
            if (sid != undefined && sid != null && sid != "") {
                this._guid = atob(sid);
                this.applicationParams.sessionId = sid;
            }

            else {
                if (this._guid == "" || this._guid == null || this._guid == undefined) {
                    this._guid = Guid.create().toString();
                    this.applicationParams.sessionId = this.encodedGuid;
                }
            }
        }

        this._storageKey = "ovs.params" + this._guid;

        if (this.hasEmptyParams && this.hasStoredParams) {
            this.restore();
        }

        if (this.hasEmptyParams) {
            this._applicationParams.token = <string>this.getParameterByName(params, "token", false, String);
            this._applicationParams.partnerId = <string>this.getParameterByName(params, "partnerId", false, String);
            this._applicationParams.nameId = <string>this.getParameterByName(params, "nameId", false, String);
            this._applicationParams.cobrandId = <string>this.getParameterByName(params, "cobrandId", false, String);
            this._applicationParams.agentId = <string>this.getParameterByName(params, "agentId", false, String);
            this._applicationParams.affiliatePin = <string>this.getParameterByName(params, "affiliatePin", false, String);
            this._applicationParams.authCode = <string>this.getParameterByName(params, "authorizationCode", false, String);
            this._applicationParams.certNumber = <string>this.getParameterByName(params, "certificateNumber", false, String);

            this._resortListingParams.searchId = <string>this.getParameterByName(params, "cmd_searchid", false, String);
            this._resortListingParams.isDirect = <boolean>this.getParameterByName(params, "cmd", false, Boolean);
            this._resortListingParams.searchDate = this.getParameterByName(params, "cmd_date", false, Date);
            this._resortListingParams.searchText = <string>this.getParameterByName(params, "cmd_text", false, String);
            this._resortListingParams.latitude = <number>this.getParameterByName(params, "cmd_lat", false, Number);
            this._resortListingParams.longitude = <number>this.getParameterByName(params, "cmd_lng", false, Number);

            this._resortDetailParams.resortId = <string>this.getParameterByName(params, "resortId", false, String);
            this._resortDetailParams.startDate = this.getParameterByName(params, "startDate", false, Date);

        }
        if (!this.hasEmptyParams) this.store();

        if (this.resortDetailParams.startDate !== undefined && this.resortDetailParams.startDate !== null)
            (<any>window).utag_data['startDate'] = this.resortDetailParams.startDate.toLocaleDateString();
    }

    public setParams(partnerId: string, nameId: string, agentId: string, cobrandId: string, affiliatePin: string, authcode: string, certnumber: string, timezoneOffset: number): void {
        this._partnerId = partnerId;
        this._nameId = nameId;
        this._cobrandId = cobrandId;
        this._agentId = agentId;
        this._affiliatePin = affiliatePin;
        this._authcode = authcode;
        this._certnumber = certnumber;
        this._timezoneOffset = timezoneOffset;
    }

    constructor(private storageService: StorageService) {

    }

    public store(): void {
        this.storageService.hydrate(this._storageKey, {
            applicationParams: this.applicationParams,
            resortDetailParams: this.resortDetailParams,
            resortListingParams: this.resortListingParams,
            checkoutParams: this.checkoutParams
        });

    }
    public get hasStoredParams(): boolean {
        return this.storageService.hasHydratedData(this._storageKey);
    }

    public restore(): void {
        var qryParams = this.storageService.dehydate(this._storageKey);
        if (qryParams == undefined || qryParams == null) alert("session has expired.")
        if (qryParams) {
            this._applicationParams = qryParams.applicationParams;
            this._resortDetailParams.restore(qryParams.resortDetailParams);
            this._resortListingParams.restore(qryParams.resortListingParams);
            this._checkoutParams.restore(qryParams.checkoutParams);
        }
    }

    public clear(): void {
        this.storageService.erase(this._storageKey);
    }
}

@Injectable()
export class UrlService {
    private _urls: KeyValuePair<any, string>[] = [];
    private _rootUrl: string;

    public get rootUrl(): string {
        return this._rootUrl;
    }

    public register(serviceImplementationType: any, url?: string): void {
        if (String.isNullOrWhiteSpace(url))
            url = this._rootUrl;

        this._urls.add(new KeyValuePair(serviceImplementationType, url));
    }

    public getUrl(serviceImplementationType: any): string {
        let pair: KeyValuePair<any, string> = this._urls.firstOrDefault(p => p.key == serviceImplementationType);

        if (!pair || String.isNullOrWhiteSpace(pair.value))
            return this._rootUrl;

        return pair.value;
    }

    public setRootUrl(rootUrl: string): void {
        this._rootUrl = rootUrl;
    }
}

class KeyValuePair<TKey, TValue>
{
    private _key: TKey;
    private _value: TValue;

    constructor(key: TKey, value: TValue) {
        this._key = key;
        this._value = value;
    }

    public get key(): TKey { return this._key; }
    public get value(): TValue { return this._value; }
}

export type ChangeListenerCallback<T> = (oldValue: any, newValue: any, property?: PropertyExpression<T>) => void

interface IChangeListener {
    propertyName: string;
    enabled: boolean;
    property: PropertyExpression<any>;
    evaluate(changes: SimpleChanges): void;
    change(change: SimpleChange): void;
}

class ChangeListener<T> implements IChangeListener {
    private _callback: ChangeListenerCallback<T>;

    public propertyName: string;
    public enabled: boolean;
    public property: PropertyExpression<T>;

    constructor(property: PropertyExpression<T>, callback: ChangeListenerCallback<T>, enabled: boolean) {
        this.property = property;
        this.propertyName = Expressions.getPropertyName(property);
        //console.log("Change listener created for:  " + this.propertyName);
        this.enabled = enabled;
        this._callback = callback;
    }

    public evaluate(changes: SimpleChanges): void {
        let change: SimpleChange = changes[this.propertyName];
        if (change) {
            this.change(change);
        }
    }

    public change(change: SimpleChange): void {
        //console.log("Change listener detected change for:  " + this.propertyName);
        if (change.previousValue !== change.currentValue)
            this._callback(change.previousValue, change.currentValue, this.property);
    }
}

export abstract class OvsComponent implements OnDestroy, OnInit, OnChanges {
    private _subscriptions: Subscription[] = [];
    private _subscriptionsBySequence: any = {};
    private _subscriptionSequence: number = 1;
    private _changeListeners: IChangeListener[] = [];
    private _route: ActivatedRoute;
    private _routerInternal: Router;
    private _errorHandler: ErrorHandler;

    public configuration: ApplicationConfiguration;
    public currencyCode: string;
    public locale: string;
    constructor() {
        // To enable use to easily refactor this base class in the future, like when we need a new service from the injector,
        // we are going to NOT use constructor injection, instead we will use the injector directly.

        // WARNING:  As with most components, don't initialize here...initialize in the ngOnInit for this class or initialize method in classes that extend this class.
    }

    private onConfigurationChanged(configuration: ApplicationConfiguration): void {
        this.configuration = configuration;
        this.currencyCode = this.configuration.CurrencyCode;
        this.locale = this.configuration.Locale;
        this.configurationChanged();
    }

    protected configurationChanged(): void {
    }

    protected listenForChange<T>(property: PropertyExpression<T>, callback: ChangeListenerCallback<T>, enabled: boolean = true): void {
        let listener: ChangeListener<T> = new ChangeListener<T>(property, callback, enabled);
        this._changeListeners.add(listener);
    }

    protected subscribe<T>(observable: Observable<T>, callback: (value: T) => void): string {
        let subscription: Subscription = observable.subscribe(callback);
        this._subscriptions.push(subscription);
        // The index in the array may change...so use a different means of creating a key that can be passed to the caller and used later to unsubscribe
        // without giving them the actual subscription.
        let sequence: string = this._subscriptionSequence.toString();
        this._subscriptionSequence++;
        this._subscriptionsBySequence[sequence] = subscription;
        return sequence;
    }

    protected setProperty<T>(property: () => T, newValue: T): void {
        let name: string = Expressions.getPropertyName(property);
        let oldValue: T = this[name];
        this[name] = newValue;
        let listener: IChangeListener = this._changeListeners.firstOrDefault(l => l.propertyName.toLowerCase() == name.toLowerCase());
        if (listener) {
            let change: SimpleChange = new SimpleChange(oldValue, newValue, false);
            listener.change(change);
        }
    }

    protected propertyChanged<T>(property: (() => T) | string, oldValue: T, newValue: T): void {
        let name;
        if (property instanceof Function) {
            name = Expressions.getPropertyName(property);
        }
        else {
            name = property;
        }

        let listener: IChangeListener = this._changeListeners.firstOrDefault(l => l.propertyName.toLowerCase() == name.toLowerCase());
        if (listener) {
            let change: SimpleChange = new SimpleChange(oldValue, newValue, false);
            listener.change(change);
        }
    }

    protected getParameterByName<T>(params: { [key: string]: any; }, name: string, isRequired: boolean = false): T {
        let result: string;

        name = name.toLowerCase();
        for (let key in params) {
            if (key.toLowerCase() === name)
                result = params[key];
        }

        if (isRequired && (!result || String.isNullOrWhiteSpace(result)))
            throw new Error("Parameter missing:  " + name);

        if (result === undefined)
            result = "";

        return <any>result;
    }

    protected unsubscribe(key: string): void {
        this._subscriptionsBySequence[key].unsubscribe();
    }

    ngOnInit() {
        this._route = Core.current.getRequiredService<ActivatedRoute>(ActivatedRoute);
        this._routerInternal = Core.current.getRequiredService<Router>(Router);
        this._errorHandler = Core.current.getRequiredService<ErrorHandler>(ErrorHandler);
        this.subscribe(Core.current.configuration, c => this.onConfigurationChanged(c));
        this.initialize();
    }

    ngOnDestroy() {
        this.dispose();
    }

    ngOnChanges(changes: SimpleChanges) {
        this._changeListeners.forEach(l => l.evaluate(changes));
    }

    protected abstract initialize(): void;

    protected dispose(): void {
        if (this._subscriptions) {
            this._subscriptions.forEach(i => i.unsubscribe());
            this._subscriptions = null;
            this._subscriptionsBySequence = null;
        }
    }

    public goBack(): void {
        window.history.back();
    }

    public goForward(): void {
        window.history.forward();
    }

    public routeTo(path: string, query?: { [key: string]: any; }): void {
        let extras: NavigationExtras;

        if (query) {
            extras = { queryParams: query };
        }

        this._routerInternal.navigateByUrl(path, extras);
    }

    protected getParameter<T>(name: string, type: { new(): T; }): T {
        let value: any;

        name = name.toLowerCase();
        for (let key in this._route.snapshot.queryParams) {
            if (key.toLowerCase() === name) {
                value = this._route.snapshot.queryParams[key];
                break;
            }
        }

        if (!value) {
            if (<any>type === String)
                return <any>new String("");

            if (<any>type === Boolean)
                return <any>new Boolean(false);

            if (<any>type === Number)
                return <any>new Number(0);

            return value;
        }

        if (<any>type === String)
            return value;

        if (<any>type === Boolean)
            return <any>new Boolean(value);

        if (<any>type === Date)
            return <any>new Date(Date.parse(value));

        if (<any>type === Number)
            return <any>new Number(value);

        let typedValue: T = new type();

        if (TypeHelpers.IsModel(typedValue)) {
            let entity: Entity = <Entity><any>typedValue;
            entity.mapFrom(value);
            return typedValue;
        }

        return JSON.parse(value);
    }


    protected getRequiredParameter<T>(name: string, type: { new(): T; }): T {
        let value: any;

        name = name.toLowerCase();
        for (let key in this._route.snapshot.queryParams) {
            if (key.toLowerCase() === name) {
                value = this._route.snapshot.queryParams[key];
                break;
            }
        }

        if (!value)
            this.handleError(new Error("Required parameter '" + name + "' not found."));

        if (<any>type === String)
            return value;

        if (<any>type === Boolean)
            return <any>new Boolean(value);

        if (<any>type === Date)
            return <any>new Date(Date.parse(value));

        if (<any>type === Number)
            return <any>new Number(value);

        let typedValue: T = new type();

        if (TypeHelpers.IsModel(typedValue)) {
            let entity: Entity = <Entity><any>typedValue;
            entity.mapFrom(value);
            return typedValue;
        }

        return JSON.parse(value);
    }

    protected handleError(error: any): void {
        this._errorHandler.handleError(error);
    }

    public hasFeedback(control: FormControl | AbstractControl): boolean {
        if (!control)
            return false;

        return control.errors && (control.dirty || control.touched);
    }

    public scrollToTop(): void {
        window.scroll(0, 0);
        //this.scroll();
    }

    private scroll(): void {
        var currentScroll = document.documentElement.scrollTop || document.body.scrollTop;
        if (currentScroll > 0) {
            window.requestAnimationFrame(() => this.scroll());
            window.scrollTo(0, currentScroll - (currentScroll / 5));
        }
    }
}

export abstract class ClientBase {
    private _http: HttpClient;
    private _rootUrl: string;
    private _errorHandler: ErrorHandler;

    // Since this is lower level core functionality and we want to make sure that any future changes don't impact consumers of core, we are going to bypass the normal constructor
    // injection and use the root Injector (located in core).  This way if we have changes to what we need in ClientBase, it does not impact classes that inherit from it.
    constructor() {
    }

    private get httpClient(): HttpClient {
        if (!this._http) {
            this._http = Core.current.getRequiredService<HttpClient>(HttpClient);
        }
        return this._http;
    }

    private get rootUrl(): string {
        if (!this._rootUrl) {
            this._rootUrl = Core.current.getRequiredService<UrlServiceBase>(UrlServiceBase).getUrl((<any>this).constructor);

            if (!this._rootUrl.endsWith("/"))
                this._rootUrl += "/";
        }
        return this._rootUrl;
    }

    private get errorHandler(): ErrorHandler {
        if (!this._errorHandler) {
            this._errorHandler = Core.current.getRequiredService<ErrorHandler>(ErrorHandler);
        }
        return this._errorHandler;
    }

    protected baseGet<T extends Entity>(url: string, typeOfT: any, params: any, success?: (value: T) => void, error?: (error: any) => void): void {
        if (!error)
            error = (e: any) => {
                this.errorHandler.handleError(e);
            };

        let httpParams: HttpParams = new HttpParams({ encoder: new OvsParameterCodec() });

        for (let param in params) {
            httpParams = httpParams.append(param, params[param]);
        }

        let headers: HttpHeaders = new HttpHeaders();
        let core: Core = Core.current;
        if (core) {
            if (core.currentConfiguration && core.currentConfiguration.SessionToken) {
                headers = headers.append("OVS-Session", core.currentConfiguration.SessionToken);
            }
        }

        this.httpClient.get<T>(this.rootUrl + url, { params: httpParams, headers: headers, observe: "response", withCredentials: true })
            .pipe(
                catchError(err => {
                    return ErrorObservable.create(err);
                })
            ).subscribe(
            (value: HttpResponse<T>) => {
                if (success) {
                    if (value.ok) {
                        if (!typeOfT) {
                            success(value.body);
                            return;
                        }

                        let result: T = new typeOfT();
                        result.mapFrom(value.body);
                        success(result);
                    }
                }
            },error);
    }

    protected baseGetArray<T extends Entity>(url: string, typeOfT: any, params: any, success?: (value: Array<T>) => void, error?: (error: any) => void): void {
        if (!error)
            error = (e: any) => {
                this.errorHandler.handleError(e);
            };

        let httpParams: HttpParams = new HttpParams({ encoder: new OvsParameterCodec() });

        for (let param in params) {
            httpParams = httpParams.append(param, params[param]);
        }

        let headers: HttpHeaders = new HttpHeaders();
        let core: Core = Core.current;
        if (core) {
            if (core.currentConfiguration && core.currentConfiguration.SessionToken) {
                headers = headers.append("OVS-Session", core.currentConfiguration.SessionToken);
            }
        }

        this.httpClient.get<Array<T>>(this.rootUrl + url, { params: httpParams, headers: headers, observe: "response", withCredentials: true })
            .pipe(
                catchError(err => {
                    return ErrorObservable.create(err);
                })
            ).subscribe(
                (value: HttpResponse<Array<T>>) => {
                    if (success) {
                        if (value.ok) {
                            let array: Array<T> = [];
                            for (let index: number = 0; index < value.body.length; index++) {
                                let result: T = new typeOfT();
                                result.mapFrom(value.body[index]);
                                array.add(result);
                            }
                            success(array);
                        }
                    }
                },error)
    }

    protected basePost<T extends Entity>(url: string, typeOfT: any, body: any, success?: (value: T) => void, error?: (error: any) => void): void {
        if (!error)
            error = (e: any) => { this.errorHandler.handleError(e); };

        let headers: HttpHeaders = new HttpHeaders();
        let core: Core = Core.current;
        if (core) {
            if (core.currentConfiguration && core.currentConfiguration.SessionToken) {
                headers = headers.append("OVS-Session", core.currentConfiguration.SessionToken);
            }
        }

        this.httpClient.post<T>(this.rootUrl + url, body, { headers: headers, observe: "body", responseType: "json", withCredentials: true })
            .pipe(
                catchError(err => {
                    return ErrorObservable.create(err);
                })
            ).subscribe(
            (value: T) => {
                if (success) {
                    if (!typeOfT) {
                        success(value);
                        return;
                    }

                    let result: T = new typeOfT();
                    result.mapFrom(value);
                    success(result);
                }
            },
            error);
    }

    protected basePut<T extends Entity>(url: string, typeOfT: any, body: any, success?: (value: T) => void, error?: (error: any) => void): void {
        if (!error)
            error = (e: any) => { this.errorHandler.handleError(e); };

        let headers: HttpHeaders = new HttpHeaders();
        let core: Core = Core.current;
        if (core) {
            if (core.currentConfiguration && core.currentConfiguration.SessionToken) {
                headers = headers.append("OVS-Session", core.currentConfiguration.SessionToken);
            }
        }

        this.httpClient.put<T>(this.rootUrl + url, body, { headers: headers, observe: "body", responseType: "json", withCredentials: true })
            .pipe(
                catchError(err => {
                    return ErrorObservable.create(err);
                })
            ).subscribe(
            (value: T) => {
                if (success) {
                    if (!typeOfT) {
                        success(value);
                        return;
                    }

                    let result: T = new typeOfT();
                    result.mapFrom(value);
                    success(result);
                }
            },
            error);
    }
}

export class OvsParameterCodec implements HttpParameterCodec {
    encodeKey(key: string): string {
        return encodeURIComponent(key);
    }

    encodeValue(value: string): string {
        return encodeURIComponent(value);
    }

    decodeKey(key: string): string {
        return decodeURIComponent(key);
    }

    decodeValue(value: string): string {
        return decodeURIComponent(value);
    }
}

@Pipe({ name: 'join' })
export class JoinPipe implements PipeTransform {
    transform(array: any[], separator: string) {
        if (!array || !array.length)
            return "";

        return array.join(separator);
    }
}


@Pipe({ name: 'ovsCurrency' })
export class OvsCurrencyPipe implements PipeTransform {
    private _innerPipe: CurrencyPipe;
    private _core: Core;
    private _rounded: string = "1.0-0";
    private _standard: string = "1.2-2";
    private _roundedFormat: string = "Rounded";
    private _standardFormat: string = "Standard";

    constructor(core: Core) {
        this._core = core;
        this._innerPipe = new CurrencyPipe(core.currentConfiguration.Locale);
    }

    public transform(value: any, digits?: string): any {
        switch (digits) {

            case this._roundedFormat:
                digits = this._rounded;
                break;
            case this._standardFormat:
                digits = this._standard;
                break;
            default:
                digits = this._rounded;
                break;
        }

        return this._innerPipe.transform(value, this._core.currentConfiguration.CurrencyCode, "symbol", digits, this._core.currentConfiguration.Locale);
    }
}

export class Queryable<T> implements IQueryable<T>
{
    private _array: T[];
    private static _initialized: boolean = false;

    constructor();
    constructor(array: T[]);
    constructor(array?: T[]) {
        if (!array)
            array = [];

        this._array = array;
    }

    public static initialize(): void {
        if (!Queryable._initialized) {
            // Array helpers
            Array.init = function (count: number, initializer: (index: number) => any): any[] {
                let list: any[] = [];

                for (let index: number = 0; index < count; index++) {
                    list.push(initializer(index));
                }

                return list;
            }

            // String helpers
            String.isNullOrEmpty = function (value: string): boolean {
                if (!value)
                    return true;

                if (!value.length)
                    return true;

                if (value.length == 0)
                    return true;

                return false;
            };
            String.isNullOrWhiteSpace = function (value: string): boolean {
                if (!value)
                    return true;

                if (!value.length)
                    return true;

                if (value.length == 0)
                    return true;

                return value.replace(/\s/g, '').length < 1;
            };
            String.prototype.containsStr = function (value: string, ignoreCase?: boolean): boolean {
                let instance: string = this;
                value = value || "";
                if (ignoreCase) {
                    value = value.toUpperCase();
                    instance = instance.toUpperCase();
                }
                return instance.indexOf(value) > -1;
            };
            String.prototype.endsWithStr = function (value: string, ignoreCase?: boolean): boolean {
                let instance: string = this;
                value = value || "";
                if (ignoreCase) {
                    value = value.toUpperCase();
                    instance = instance.toUpperCase();
                }
                return instance.slice(-value.length) === value;
            };
            String.prototype.startsWithStr = function (value: string, ignoreCase?: boolean): boolean {
                let instance: string = this;
                value = value || "";
                if (ignoreCase) {
                    value = value.toUpperCase();
                    instance = instance.toUpperCase();
                }

                if (this.length < value.length)
                    return false;

                return instance.slice(0, value.length) === value;
            };
            String.prototype.indexOfStr = function (value: string, indexOrIgnoreCase?: any, ignoreCase?: boolean): number {
                let index: number;

                if (TypeHelpers.IsNumber(indexOrIgnoreCase))
                    index = indexOrIgnoreCase;
                else if (TypeHelpers.IsBoolean(indexOrIgnoreCase))
                    ignoreCase = indexOrIgnoreCase;

                let instance: string = this;
                value = value || "";
                if (ignoreCase) {
                    value = value.toUpperCase();
                    instance = instance.toUpperCase();
                }

                return instance.indexOf(value, index);
            };
            String.prototype.replaceStr = function (oldValue: string, newValue: string): string {
                oldValue = oldValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
                return this.replace(new RegExp(oldValue, 'g'), newValue);
            };

            // Array helpers
            Array.prototype.add = function (item: any): void {
                this.push(item);
            };
            Array.prototype.addRange = function (appendItems: any[]): void {
                if (!appendItems || appendItems.length == 0)
                    return;

                for (let index: number = 0; index < appendItems.length; index++)
                    this.push(appendItems[index]);
            };
            Array.prototype.insert = function (index: number, item: any): void {
                this.splice(index, 0, item);
            };
            Array.prototype.remove = function (item: any): void {
                let index: number = this.indexOf(item);
                if (index > -1)
                    this.splice(index, 1);
            };
            Array.prototype.removeAt = function (index: number): void {
                this.splice(index, 1);
            };
            Array.prototype.asQueryable = function () { return new Queryable(this); };

            // IQueryable
            (<any>Array.prototype).all = function (arg1: any) { return new Queryable(this).all(arg1); };
            (<any>Array.prototype).any = function (arg1: any) { return new Queryable(this).any(arg1); };
            (<any>Array.prototype).append = function (arg1: any) { return new Queryable(this).append(arg1); };
            (<any>Array.prototype).average = function (arg1: any) { return new Queryable(this).average(arg1); };
            (<any>Array.prototype).concatenate = function (arg1: any) { return new Queryable(this).concatenate(arg1); };
            (<any>Array.prototype).contains = function (arg1: any, arg2: any) { return new Queryable(this).contains(arg1, arg2); };
            (<any>Array.prototype).count = function (arg1: any) { return new Queryable(this).count(arg1); };
            (<any>Array.prototype).distinct = function (arg1: any) { return new Queryable(this).distinct(arg1); };
            (<any>Array.prototype).elementAt = function (arg1: any) { return new Queryable(this).elementAt(arg1); };
            (<any>Array.prototype).elementAtOrDefault = function (arg1: any, arg2: any) { return new Queryable(this).elementAtOrDefault(arg1, arg2); };
            (<any>Array.prototype).first = function (arg1: any) { return new Queryable(this).first(arg1); };
            (<any>Array.prototype).firstOrDefault = function (arg1: any, arg2: any) { return new Queryable(this).firstOrDefault(arg1, arg2); };
            (<any>Array.prototype).last = function (arg1: any) { return new Queryable(this).last(arg1); };
            (<any>Array.prototype).lastOrDefault = function (arg1: any, arg2: any) { return new Queryable(this).lastOrDefault(arg1, arg2); };
            (<any>Array.prototype).max = function (arg1: any) { return new Queryable(this).max(arg1); };
            (<any>Array.prototype).min = function (arg1: any) { return new Queryable(this).min(arg1); };
            (<any>Array.prototype).orderBy = function (arg1: any) { return new Queryable(this).orderBy(arg1); };
            (<any>Array.prototype).orderByDescending = function (arg1: any) { return new Queryable(this).orderByDescending(arg1); };
            (<any>Array.prototype).prepend = function (arg1: any) { return new Queryable(this).prepend(arg1); };
            (<any>Array.prototype).select = function (arg1: any) { return new Queryable(this).select(arg1); };
            (<any>Array.prototype).single = function (arg1: any) { return new Queryable(this).single(arg1); };
            (<any>Array.prototype).singleOrDefault = function (arg1: any, arg2: any) { return new Queryable(this).singleOrDefault(arg1, arg2); };
            (<any>Array.prototype).skip = function (arg1: any) { return new Queryable(this).skip(arg1); };
            (<any>Array.prototype).skipWhile = function (arg1: any) { return new Queryable(this).skipWhile(arg1); };
            (<any>Array.prototype).sum = function (arg1: any) { return new Queryable(this).sum(arg1); };
            (<any>Array.prototype).take = function (arg1: any) { return new Queryable(this).take(arg1); };
            (<any>Array.prototype).takeWhile = function (arg1: any) { return new Queryable(this).takeWhile(arg1); };
            (<any>Array.prototype).where = function (arg1: any) { return new Queryable(this).where(arg1); };
            Queryable._initialized = true;
        }
    }

    public all(predicate: (item: T) => boolean): boolean {
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            if (!predicate(item))
                return false;
        }
        return true;
    }

    public any(predicate: (item: T) => boolean): boolean {
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            if (predicate(item))
                return true;
        }
        return false;
    }

    public append(item: T): IQueryable<T> {
        var newItems: T[] = this._array.slice();
        newItems.push(item);
        return new Queryable<T>(newItems);
    }

    public average(selector?: (item: T) => number): number {
        if (this._array.length == 0)
            return 0;

        if (!selector) {
            var sum: number = this.sum();
            return sum / this._array.length;
        }
        else {
            var sum: number = this.sum(selector);
            return sum / this._array.length;
        }
    }

    public concatenate(items: T[]): IQueryable<T> {
        var newItems: T[] = this._array.concat(items);
        return new Queryable<T>(newItems);
    }

    public contains(value: T, equalityComparer?: (lhs: T, rhs: T) => boolean): boolean {
        if (equalityComparer) {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (equalityComparer(item, value))
                    return true;
            }
        }
        else {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (item == value)
                    return true;
            }
        }
        return false;
    }

    public count(predicate?: (item: T) => boolean): number {
        if (!predicate) {
            return this._array.length;
        }
        else {
            var count: number = 0;
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (predicate(item))
                    count++;
            }
            return count;
        }
    }

    public distinct(comparer?: (lhs: T, rhs: T) => boolean): IQueryable<T> {
        var newItems: T[] = [];

        if (comparer) {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                let exists: boolean = false;
                for (var newIndex: number = 0; newIndex < newItems.length; newIndex++) {
                    let newItem: T = newItems[newIndex];
                    if (comparer(item, newItem)) {
                        exists = true;
                        break;
                    }
                }
                if (!exists)
                    newItems.push(item);
            }
        }
        else {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                let exists: boolean = false;
                for (var newIndex: number = 0; newIndex < newItems.length; newIndex++) {
                    let newItem: T = newItems[newIndex];
                    if (item == newItem) {
                        exists = true;
                        break;
                    }
                }
                if (!exists)
                    newItems.push(item);
            }
        }

        return new Queryable<T>(newItems);
    }

    public elementAt(index: number): T {
        if (index >= 0 && index < this._array.length)
            return this._array[index];

        throw new Error("Sequence contains no matching element.");
    }

    public elementAtOrDefault(index: number, defaultValue?: T): T;
    public elementAtOrDefault(index: number, defaultValue?: () => T): T;
    public elementAtOrDefault(index: number, defaultValue?: any): T {
        if (index >= 0 && index < this._array.length)
            return this._array[index];

        if (defaultValue === undefined)
            defaultValue = null;

        if (typeof defaultValue === "function")
            return defaultValue();

        return defaultValue;
    }

    public first(predicate?: (item: T) => boolean): T {
        if (!predicate) {
            if (this._array.length > 0)
                return this._array[0];
        }
        else {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (predicate(item))
                    return item;
            }
        }

        throw new Error("Sequence contains no matching element.");
    }

    public firstOrDefault(predicate?: (item: T) => boolean, defaultValue?: T): T;
    public firstOrDefault(predicate?: (item: T) => boolean, defaultValue?: () => T): T;
    public firstOrDefault(predicate?: (item: T) => boolean, defaultValue?: any): T {
        if (!predicate) {
            if (this._array.length > 0)
                return this._array[0];
        }
        else {
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (predicate(item))
                    return item;
            }
        }

        if (defaultValue === undefined)
            defaultValue = null;

        if (typeof defaultValue === "function")
            return defaultValue();

        return defaultValue;
    }

    public last(predicate?: (item: T) => boolean): T {
        if (!predicate) {
            if (this._array.length > 0)
                return this._array[this._array.length - 1];
        }
        else {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: T = this._array[index];
                if (predicate(item))
                    return item;
            }
        }

        throw new Error("Sequence contains no matching element.");
    }

    public lastOrDefault(predicate?: (item: T) => boolean, defaultValue?: T): T;
    public lastOrDefault(predicate?: (item: T) => boolean, defaultValue?: () => T): T;
    public lastOrDefault(predicate?: (item: T) => boolean, defaultValue?: any): T {
        if (!predicate) {
            if (this._array.length > 0)
                return this._array[this._array.length - 1];
        }
        else {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: T = this._array[index];
                if (predicate(item))
                    return item;
            }
        }

        if (defaultValue === undefined)
            defaultValue = null;

        if (typeof defaultValue === "function")
            return defaultValue();

        return defaultValue;
    }

    public max(selector?: (item: T) => number): number {
        if (this._array.length == 0)
            return 0;

        var max: number | null = null;

        if (!selector) {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = <any>this._array[index];
                if (max == null || item > max)
                    max = item;
            }
        }
        else {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = selector(this._array[index]);
                if (max == null || item > max)
                    max = item;
            }
        }

        return max == null ? 0 : max;
    }

    public min(selector?: (item: T) => number): number {
        if (this._array.length == 0)
            return 0;

        var min: number | null = null;

        if (!selector) {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = <any>this._array[index];
                if (min == null || item < min)
                    min = item;
            }
        }
        else {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = selector(this._array[index]);
                if (min == null || item < min)
                    min = item;
            }
        }

        return min == null ? 0 : min;
    }

    public orderBy<TKey>(keySelector: (item: T) => TKey): IQueryable<T> {
        var newItems: T[] = this._array.slice();
        newItems.sort((a, b) => {
            var lhs: TKey = keySelector(a);
            var rhs: TKey = keySelector(b);
            if (lhs < rhs)
                return -1;
            else if (lhs > rhs)
                return 1;
            else
                return 0;
        });
        return new Queryable<T>(newItems);
    }

    public orderByDescending<TKey>(keySelector: (item: T) => TKey): IQueryable<T> {
        var newItems: T[] = this._array.slice();
        newItems.sort((a, b) => {
            var lhs: TKey = keySelector(a);
            var rhs: TKey = keySelector(b);
            if (lhs < rhs)
                return 1;
            else if (lhs > rhs)
                return -1;
            else
                return 0;
        });
        return new Queryable<T>(newItems);
    }

    public prepend(item: T): IQueryable<T> {
        var newItems: T[] = this._array.slice();
        newItems.splice(0, 0, item);
        return new Queryable<T>(newItems);
    }

    public select<TResult>(selector: (item: T, index?: number) => TResult): IQueryable<TResult> {
        var newItems: TResult[] = [];
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            let newItem: TResult = selector(item, index);
            newItems.push(newItem);
        }
        return new Queryable<TResult>(newItems);
    }

    public single(predicate?: (item: T) => boolean): T {
        if (!predicate) {
            if (this._array.length > 1)
                throw new Error("Sequence contains more than one element.");
            else if (this._array.length > 0)
                return this._array[0];
        }
        else {
            var match: T | null = null;
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (predicate(item)) {
                    if (match == null)
                        match = item;
                    else
                        throw new Error("Sequence contains more than one matching element.");
                }
            }
            if (match != null)
                return match;
        }

        throw new Error("Sequence contains no matching element.");
    }

    public singleOrDefault(predicate?: (item: T) => boolean, defaultValue?: T): T;
    public singleOrDefault(predicate?: (item: T) => boolean, defaultValue?: () => T): T;
    public singleOrDefault(predicate?: (item: T) => boolean, defaultValue?: any): T {
        if (!predicate) {
            if (this._array.length > 1)
                throw new Error("Sequence contains more than one element.");
            else if (this._array.length > 0)
                return this._array[0];
        }
        else {
            var match: T | null = null;
            for (var index: number = 0; index < this._array.length; index++) {
                let item: T = this._array[index];
                if (predicate(item)) {
                    if (match == null)
                        match = item;
                    else
                        throw new Error("Sequence contains more than one matching element.");
                }
            }
            if (match != null)
                return match;
        }

        if (typeof defaultValue === "function")
            return defaultValue();

        return defaultValue;
    }

    public skip(count: number): IQueryable<T> {
        if (count <= 0)
            return this;

        return new Queryable<T>(this._array.slice(count - 1));
    }

    public skipWhile(predicate: (item: T, index?: number) => boolean): IQueryable<T> {
        var newItems: T[] = [];
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            if (predicate(item, index))
                continue;

            return new Queryable<T>(this._array.slice(index));
        }
        return new Queryable<T>(newItems);
    }

    public sum(selector?: (item: T) => number): number {
        if (this._array.length == 0)
            return 0;

        var sum: number = 0;

        if (!selector) {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = <any>this._array[index];
                sum += item;
            }
        }
        else {
            for (var index: number = this._array.length - 1; index >= 0; index--) {
                let item: number = selector(this._array[index]);
                sum += item;
            }
        }

        return sum;
    }

    public take(count: number): IQueryable<T> {
        if (count <= 0)
            return new Queryable<T>([]);

        return new Queryable<T>(this._array.slice(0, count));
    }

    public takeWhile(predicate: (item: T, index?: number) => boolean): IQueryable<T> {
        var newItems: T[] = [];
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            if (!predicate(item, index))
                break;

            newItems.push(item);
        }
        return new Queryable<T>(newItems);
    }

    public where(predicate: (item: T, index?: number) => boolean): IQueryable<T> {
        var newItems: T[] = [];
        for (var index: number = 0; index < this._array.length; index++) {
            let item: T = this._array[index];
            if (predicate(item, index))
                newItems.push(item);
        }
        return new Queryable<T>(newItems);
    }

    private typeOf(type: { new(): T }): string {
        return typeof type;
    }

    public toArray(): Array<T> {
        return this._array;
    }
}

export type PropertyExpression<T> = () => T;

export class Expressions {
    public static getPropertyName(expression: PropertyExpression<any>): string {
        if ((<any>expression).__propertyName) {
            return (<any>expression).__propertyName;
        }

        let name: string = expression.toString().split(".").last();
        let stopIndex: number = name.indexOf(";");
        if (stopIndex == -1)
            stopIndex = name.indexOf("}");

        if (stopIndex == -1)
            throw new Error("Property name not found in:  " + expression.toString() + ".  Ensure format of expression ends with a property reference.");

        name = name.substring(0, stopIndex).trim();
        (<any>expression).__propertyName = name;
        return name;
    }
}

export class TypeHelpers {
    public static IsNumber(value: any): boolean {
        let type: string = typeof value;
        return type === "number" || type === "Number";
    }

    public static IsString(value: any): boolean {
        let type: string = typeof value;
        return type === "string" || type === "String";
    }

    public static IsBoolean(value: any): boolean {
        let type: string = typeof value;
        return type === "boolean" || type === "Boolean";
    }

    public static IsDate(value: any): boolean {
        let type: string = typeof value;
        return type === "Date";
    }

    public static IsModel(value: any): boolean {
        return value ? value.mapFrom : false;
    }
}

export abstract class Entity {
    constructor() {
    }

    public mapFrom(source: any): void {
    }

    public static mapToDate(value: any): Date {
        if (!value)
            return null;

        if (value instanceof Date)
            return value;

        return new Date(Date.parse(value));
    }

    public static mapToEntity<T extends Entity>(type: any, value: any): T {
        if (!value)
            return null;

        let entity: T = new type();
        entity.mapFrom(value);
        return entity;
    }

    public static mapToEntityArray<T extends Entity>(type: any, value: any): T[] {
        if (!value)
            return null;

        if (!value.length)
            return value;

        let array: T[] = [];
        for (let index: number = 0; index < value.length; index++) {
            let entity: T = new type();
            entity.mapFrom(value[index]);
            array.add(entity);
        }

        return array;
    }
}

export abstract class ValidationServicesBase {
    public abstract isValidCreditCardNumber(creditCardNumber: string, creditCardType: CardTypes): boolean;
    public abstract creditCardValidator(creditCardType: CardTypes): ValidatorFn;
}

export class ValidationServices extends ValidationServicesBase {
    public isValidCreditCardNumber(creditCardNumber: string, creditCardType: CardTypes): boolean {
        if (!creditCardNumber || !creditCardType)
            return false;

        let ccNumber = creditCardNumber.replace(/[" "]*/g, "");

        if (ccNumber.length < 13)
            return false;

        switch (creditCardType) {
            case CardTypes.Visa:
                if (!this.isValidVisa(ccNumber)) return false;
                break;
            case CardTypes.MasterCard:
                if (!this.isValidMasterCard(ccNumber)) return false;
                break;
            case CardTypes.Discover:
                if (!this.isValidDiscover(ccNumber)) return false;
                break;
            case CardTypes.AmericanExpress:
                if (!this.isValidAmericanExpress(ccNumber)) return false;
                break;
            case CardTypes.DinersClub:
                if (!this.isValidDinersClub(ccNumber)) return false;
                break;
            case CardTypes.MilitaryStarCard:
                if (!this.isValidMilitaryStarCard(ccNumber)) return false;
                break;
            case CardTypes.NEACreditCard:
                if (!this.isValidMasterCard(ccNumber)) return false;
                break;
            default:
                return false;
        }

        return true;
    }

    private isValidCreditCard(creditCardNumber: string) {
        if (creditCardNumber.length > 19)
            return (false);

        let sum = 0;
        let mul = 1;
        let l = creditCardNumber.length;

        for (let i = 0; i < l; i++) {
            let digit = creditCardNumber.substring(l - i - 1, l - i);
            let tproduct = parseInt(digit, 10) * mul;
            if (tproduct >= 10)
                sum += (tproduct % 10) + 1;
            else
                sum += tproduct;

            if (mul == 1)
                mul++;
            else
                mul--;
        }

        return (sum % 10) == 0;
    }

    private isValidAmericanExpress(creditCardNumber: string): boolean {
        let firstdig: string = creditCardNumber.substring(0, 1);
        let seconddig: string = creditCardNumber.substring(1, 2);

        if ((creditCardNumber.length == 15) && (firstdig == "3") && ((seconddig == "4") || (seconddig == "7")))
            return this.isValidCreditCard(creditCardNumber);

        return false;
    }

    private isValidDinersClub(creditCardNumber: string): boolean {
        let firstdig: string = creditCardNumber.substring(0, 1);
        let seconddig: string = creditCardNumber.substring(1, 2);

        return ((creditCardNumber.length == 14) && (firstdig == "3") && ((seconddig == "0") || (seconddig == "6") || (seconddig == "8")));
    }

    private isValidDiscover(creditCardNumber: string): boolean {
        let first4digs: string = creditCardNumber.substring(0, 4);
        if ((creditCardNumber.length == 16) && (first4digs == "6011"))
            return this.isValidCreditCard(creditCardNumber);

        return false;
    }

    private isValidMasterCard(creditCardNumber: string): boolean {
        let firstdig: string = creditCardNumber.substring(0, 1);
        let seconddig: number = parseInt(creditCardNumber.substring(1, 2));

        if ((creditCardNumber.length == 16) && (firstdig == "5") && ((seconddig >= 1) && (seconddig <= 5)))
            return this.isValidCreditCard(creditCardNumber);

        return false;
    }

    private isValidMilitaryStarCard(creditCardNumber: string): boolean {
        let first4digs: string = creditCardNumber.substring(0, 4);
        return ((creditCardNumber.length == 16) && (first4digs == "6019"));
    }

    private isValidVisa(creditCardNumber: string): boolean {
        if (((creditCardNumber.length == 16) || (creditCardNumber.length == 13)) && (creditCardNumber.substring(0, 1) == "4"))
            return this.isValidCreditCard(creditCardNumber);

        return false;
    }

    public creditCardValidator(creditCardType: CardTypes): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } => {

            if (control.value != null && control.value.length >= 6) {
                creditCardType = this.determineCardType(control.value, 11);
                if ((creditCardType <= CardTypes.Unknown))
                    return { 'creditcardtype': { value: control.value } };
            }
            let isValid: boolean = this.isValidCreditCardNumber(control.value, creditCardType);
            return !isValid ? { 'creditCard': { value: control.value } } : null;
        };
    }

    private determineCardType(cardNumber: string, merchantVersion?: number) {

        if (cardNumber == undefined || cardNumber.length == 0) {
            return CardTypes.Unknown;
        }

        if (cardNumber.substring(0, 1) == "4") {
            return CardTypes.Visa;
        }

        if (cardNumber.length >= 2) {

            switch (cardNumber.substring(0, 2)) {
                case "34":
                case "37":
                    return CardTypes.AmericanExpress;
            }

            if (merchantVersion != 10) {
                switch (cardNumber.substring(0, 2)) {
                    case "51":
                    case "52":
                    case "53":
                    case "54":
                    case "55":
                        return CardTypes.MasterCard;
                    case "64":
                    case "65":
                        return CardTypes.Discover;
                }
            }

            if (cardNumber.length >= 3) {
                switch (cardNumber.substring(0, 3)) {
                    case "622":
                        return CardTypes.Discover;
                }
            }

            if (cardNumber.length >= 4) {
                switch (cardNumber.substring(0, 4)) {
                    case "6019":
                        return CardTypes.MilitaryStarCard;
                    case "6011":
                        return CardTypes.Discover;
                }
            }


        }

        if (cardNumber.length >= 6) {
            var bin = Number.parseInt(cardNumber.substring(0, 6));

            switch (bin) {
                case 380000:
                case 381000:
                case 382000:
                case 383000:
                case 504994:
                    return CardTypes.Sears;
                case 700600:
                case 777700:
                case 700801:
                    return CardTypes.SearsGiftCard;
            }

            if (merchantVersion == 10) // Sears Gift Card
            {
                if (bin >= 600000 && bin <= 689999) {
                    return CardTypes.SearsGiftCard;
                }
            }
            else {
                // Mastercard 2-Series BIN Implementation
                if (bin >= 222100 && bin <= 272099) {
                    return CardTypes.MasterCard;
                }
            }
        }

        return CardTypes.Unknown;

    }
}

@Directive({
    selector: '[creditCardType][ngModel]',
    providers: [{ provide: NG_VALIDATORS, useExisting: CreditCardValidator, multi: true }]
})
export class CreditCardValidator implements Validator {
    private _validationServices: ValidationServicesBase;

    @Input()
    public creditCardType: CardTypes;

    constructor(validationServices: ValidationServicesBase) {
        this._validationServices = validationServices;
    }

    validate(control: AbstractControl): { [key: string]: any; } {
        return this._validationServices.creditCardValidator(this.creditCardType)(control);
    }


}

//
//      Add generated core types below here.
//

export enum TargetEnvironment {
    None = 0,
    Development = 1,
    QA = 2,
    Staging = 3,
    Production = 4
}

export class Menu extends Entity {
    public NavigationUrl: string;
    public DisplayText: string;
    public IsPopup: boolean;
    public PopupHeight: number;
    public PopupWidth: number;
    public HasTracking: boolean;
    public Menus: Array<Menu>;
    public IsSeparator: boolean;
    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.NavigationUrl = source.NavigationUrl;
        this.DisplayText = source.DisplayText;
        this.IsPopup = source.IsPopup;
        this.PopupHeight = source.PopupHeight;
        this.PopupWidth = source.PopupWidth;
        this.HasTracking = source.HasTracking;
        this.IsSeparator = source.IsSeparator;
        this.Menus = Entity.mapToEntityArray<Menu>(Menu, source.Menus);
    }
}

export class ApplicationConfiguration extends Entity {
    public Environment: TargetEnvironment;
    public User: User;
    public Member: Member;
    public Partner: Partner;
    public Labels: Array<DisplayLabel>;
    public Logos: Array<DisplayLogo>;
    public FooterLinks: Array<FooterLink>;
    public UserMenus: Array<Menu>;
    public Menus: Array<Menu>;
    public CountryCode: string;
    public CurrencyCode: string;
    public Locale: string;
    public Properties: Array<ApplicationProperty>;
    public SessionToken: string;
    public AcceptedCreditCardTypes: Array<CreditCardType>;
    public CashBackMaximum: number;
    public DbcName: string;
    public HasCashBack: boolean;
    public HasDbc: boolean;
    public HasReward: boolean;
    public HasVacationCash: boolean;
    public HasOnlineBooking: boolean;
    public HasLocaleDropDown: boolean;
    public InsuranceDecisionLabel: string;
    public InsuranceDeclinedText: string;
    public InsurancePurchasedText: string;
    public InsuranceTerms: string;
    public InsuranceLabel: string;
    public InsuranceName: string;
    public MemberLoginUrl: string;
    public IsMemberRequiredForBooking: boolean;
    public IsMemberRequiredForSearch: boolean;
    public StoreName: string;
    public PurchaseTypes: Array<PurchaseType>;
    public SellsLandInsurance: boolean;
    public ShowPricePerDay: boolean;
    public ShowRewardPerDay: boolean;
    public ShowPricing: boolean;
    public ShowRewardPercentage: boolean;
    public IsAgent: boolean;
    public PurchaseTerms: string;
    public IsClientSideTempDataPersistanceEnabled: boolean;
    public SearchLocaleList: Array<SearchLocale>;
    public AgentJwt: string;
    public SubscriberLink: string;
    public ShowOptimizely: boolean;
    public AgentUserName: string;
    public AgentEmail: string;
    public AgentFullName: string;
    public AgentAssistDate: string;
    public AgentPhoneNo: string;
    public NameId: string;
    public UseBrowserBackOnResortDetailsPage: boolean;
    public HideResortCreditMessage: boolean;
    public UseAlternateMobileNavLayout: boolean;
    public ShowCommentBoxAsPromoCodeForNonAgents: boolean;


    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Environment = source.Environment;
        this.User = Entity.mapToEntity<User>(User, source.User);
        this.Member = Entity.mapToEntity<Member>(Member, source.Member);
        this.Partner = Entity.mapToEntity<Partner>(Partner, source.Partner);
        this.Labels = Entity.mapToEntityArray<DisplayLabel>(DisplayLabel, source.Labels);
        this.Logos = Entity.mapToEntityArray<DisplayLogo>(DisplayLogo, source.Logos);
        this.FooterLinks = Entity.mapToEntityArray<FooterLink>(FooterLink, source.FooterLinks);
        this.UserMenus = Entity.mapToEntityArray<Menu>(Menu, source.UserMenus);
        this.Menus = Entity.mapToEntityArray<Menu>(Menu, source.Menus);
        this.CountryCode = source.CountryCode;
        this.CurrencyCode = source.CurrencyCode;
        this.Locale = source.Locale;
        this.Properties = Entity.mapToEntityArray<ApplicationProperty>(ApplicationProperty, source.Properties);
        this.SessionToken = source.SessionToken;
        this.AcceptedCreditCardTypes = Entity.mapToEntityArray<CreditCardType>(CreditCardType, source.AcceptedCreditCardTypes);
        this.CashBackMaximum = source.CashBackMaximum;
        this.DbcName = source.DbcName;
        this.HasCashBack = source.HasCashBack;
        this.HasDbc = source.HasDbc;
        this.HasReward = source.HasReward;
        this.HasVacationCash = source.HasVacationCash;
        this.HasOnlineBooking = source.HasOnlineBooking;
        this.HasLocaleDropDown = source.HasLocaleDropDown;
        this.InsuranceDecisionLabel = source.InsuranceDecisionLabel;
        this.InsuranceDeclinedText = source.InsuranceDeclinedText;
        this.InsurancePurchasedText = source.InsurancePurchasedText;
        this.InsuranceTerms = source.InsuranceTerms;
        this.InsuranceLabel = source.InsuranceLabel;
        this.InsuranceName = source.InsuranceName;
        this.MemberLoginUrl = source.MemberLoginUrl;
        this.IsMemberRequiredForBooking = source.IsMemberRequiredForBooking;
        this.IsMemberRequiredForSearch = source.IsMemberRequiredForSearch;
        this.SellsLandInsurance = source.SellsLandInsurance;
        this.StoreName = source.StoreName;
        this.PurchaseTypes = Entity.mapToEntityArray<PurchaseType>(PurchaseType, source.PurchaseTypes);
        this.ShowPricePerDay = source.ShowPricePerDay;
        this.ShowRewardPerDay = source.ShowRewardPerDay;
        this.ShowPricing = source.ShowPricing;
        this.IsAgent = source.IsAgent;
        this.PurchaseTerms = source.PurchaseTerms;
        this.IsClientSideTempDataPersistanceEnabled = source.IsClientSideTempDataPersistanceEnabled;
        this.SearchLocaleList = Entity.mapToEntityArray<SearchLocale>(SearchLocale, source.SearchLocaleList);
        this.AgentJwt = source.AgentJwt;
        this.SubscriberLink = source.SubscriberLink;
        this.ShowOptimizely = source.ShowOptimizely;
        this.AgentUserName = source.AgentUserName;
        this.AgentEmail = source.AgentEmail;
        this.AgentFullName = source.AgentFullName;
        this.AgentAssistDate = source.AgentAssistDate;
        this.AgentPhoneNo = source.AgentPhoneNo;
        this.UseBrowserBackOnResortDetailsPage = source.UseBrowserBackOnResortDetailsPage;
        this.HideResortCreditMessage = source.HideResortCreditMessage;
        this.UseAlternateMobileNavLayout = source.UseAlternateMobileNavLayout;
        this.ShowCommentBoxAsPromoCodeForNonAgents = source.ShowCommentBoxAsPromoCodeForNonAgents;

    }
}
export class ApplicationProperty extends Entity {
    public Name: string;
    public Value: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Name = source.Name;
        this.Value = source.Value;
    }
}
export class CreditCardType extends Entity {
    public Id: number;
    public Name: string;
    public HasCvv: boolean;
    public HasExpirationDate: boolean;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.Name = source.Name;
        this.HasCvv = source.HasCvv;
        this.HasExpirationDate = source.HasExpirationDate;
    }
}
export class PurchaseType extends Entity {
    public Id: number;
    public Name: string;
    public IsDiscount: boolean;
    public Description: string;
    public MinimumPoints: number;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.Name = source.Name;
        this.IsDiscount = source.IsDiscount;
        this.Description = source.Description;
        this.MinimumPoints = source.MinimumPoints;
    }
}
export class DisplayLabel extends Entity {
    public Name: string;
    public DisplayText: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Name = source.Name;
        this.DisplayText = source.DisplayText;
    }
}
export class DisplayLogo extends Entity {
    public NavigationUrl: string;
    public AlternateText: string;
    public ImageUrl: string;
    public AltImageUrl: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.NavigationUrl = source.NavigationUrl;
        this.AlternateText = source.AlternateText;
        this.ImageUrl = source.ImageUrl;
        this.AltImageUrl = source.AltImageUrl;
    }
}
export class FooterLink extends Entity {
    public NavigationUrl: string;
    public DisplayText: string;
    public IsPopup: boolean;
    public PopupHeight: number;
    public PopupWidth: number;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.NavigationUrl = source.NavigationUrl;
        this.DisplayText = source.DisplayText;
        this.IsPopup = source.IsPopup;
        this.PopupHeight = source.PopupHeight;
        this.PopupWidth = source.PopupWidth;
    }
}

export class Member extends Entity {
    public Id: string;
    public PointBalance: number;
    public DbcBalance: number;
    public VCBalance: number;
    public FirstName: string;
    public LastName: string;
    public Address: Address;
    public PhoneNumbers: Array<Phone>;
    public EmailAddress: EmailAddress;
    public IsPrimaryMember: boolean;
    public ExpirationDate: Date;
    public EnrollmentDate: Date;
    public MemberType: string;
    public Status: string;
    public NameId: number;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.PointBalance = source.PointBalance;
        this.DbcBalance = source.DbcBalance;
        this.VCBalance = source.VCBalance;
        this.FirstName = source.FirstName;
        this.LastName = source.LastName;
        this.Address = Entity.mapToEntity<Address>(Address, source.Address);
        this.PhoneNumbers = Entity.mapToEntityArray<Phone>(Phone, source.PhoneNumbers);
        this.EmailAddress = Entity.mapToEntity<EmailAddress>(EmailAddress, source.EmailAddress);
        this.IsPrimaryMember = source.IsPrimaryMember;
        this.ExpirationDate = Entity.mapToDate(source.ExpirationDate);
        this.EnrollmentDate = Entity.mapToDate(source.EnrollmentDate);
        this.MemberType = source.MemberType;
        this.Status = source.Status;
        this.NameId = source.NameId;
    }
}
export class Address extends Entity {
    public Name: string;
    public Suffix: string;
    public Address1: string;
    public Address2: string;
    public Address3: string;
    public City: string;
    public Region: string;
    public PostalCode: string;
    public Country: string;
    public RegionCode: string;
    public CountryCode: string;
    public RegionId: number;
    public CountryId: number;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Name = source.Name;
        this.Suffix = source.Suffix;
        this.Address1 = source.Address1;
        this.Address2 = source.Address2;
        this.Address3 = source.Address3;
        this.City = source.City;
        this.Region = source.Region;
        this.PostalCode = source.PostalCode;
        this.Country = source.Country;
        this.RegionCode = source.RegionCode;
        this.CountryCode = source.CountryCode;
        this.RegionId = source.RegionId;
        this.CountryId = source.CountryId;
    }
}
export class EmailAddress extends Entity {
    public Name: string;
    public Address: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Name = source.Name;
        this.Address = source.Address;
    }
}
export class Phone extends Entity {
    public Name: string;
    public IsPrimary: boolean;
    public CountryCode: string;
    public ExchangePrefix: string;
    public Number: string;
    public IsMobile: boolean;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Name = source.Name;
        this.IsPrimary = source.IsPrimary;
        this.CountryCode = source.CountryCode;
        this.ExchangePrefix = source.ExchangePrefix;
        this.Number = source.Number;
        this.IsMobile = source.IsMobile;
    }
}
export class Partner extends Entity {
    public ShortName: string;
    public LongName: string;
    public PhoneNumber: string;
    public Email: string;
    public Id: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.ShortName = source.ShortName;
        this.LongName = source.LongName;
        this.PhoneNumber = source.PhoneNumber;
        this.Email = source.Email;
        this.Id = source.Id;
    }
}
export class User extends Entity {
    public Id: string;
    public Name: string;
    public FirstName: string;
    public LastName: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.Name = source.Name;
        this.FirstName = source.FirstName;
        this.LastName = source.LastName;
    }
}
export class GeographicalRegion extends Entity {
    public Id: number;
    public Name: string;
    public Code: string;
    public CountryId: number;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.Name = source.Name;
        this.Code = source.Code;
        this.CountryId = source.CountryId;
    }
}
export class Country extends Entity {
    public Id: number;
    public Name: string;
    public Code: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.Name = source.Name;
        this.Code = source.Code;
    }
}
export class ImagePath extends Entity {
    public Id: number;
    public ImagePathRoot: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Id = source.Id;
        this.ImagePathRoot = source.ImagePathRoot;
    }
}

export class ClientVersion extends Entity {
    public Version: string;

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.Version = source.Version;
    }
}

export class SearchLocale extends Entity {
    public CountryId: number;
    public StateId: number;
    public City: string;
    public Country: string;
    public Latitude: string;
    public Longitude: string;
    public State: string;

    public get FullLocale(): string {
        return this.City + ", " + this.State + "-" + this.Country;
    }

    public mapFrom(source: any): void {
        super.mapFrom(source);
        this.CountryId = source.CountryId;
        this.StateId = source.StateId;
        this.City = source.City;
        this.Country = source.Country;
        this.Latitude = source.Latitude;
        this.Longitude = source.Longitude;
        this.State = source.State;
    }

}


// WEBPACK FOOTER //
// /home/vsts/work/1/s/Ovs.Resort.UI.Web/src/app/shared/core/ovs.core.ts